import { ClinicInvoicePanel } from './ClinicInvoicePanel';
// Encounter charges section — original-chart Phase 3.c₁ (2026-10-04).
//
// Line-item billing on the encounter + print receipt. 3.c₁ keeps the UX
// deliberately flat: enter rows, see the running total, stamp them
// paid/void, hit Print. 3.c₂ will layer on prescription + lab request
// PDFs, Pamosi Vault / email receipt delivery, and HMO verification UX.
//
// The receipt itself is a client-side HTML view in a new tab, triggered
// by window.print(). No Puppeteer round-trip — the pilot runs on a
// Chromebook/desktop that already has print-to-PDF built in.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    EncounterCharge,
    EncounterChargeServiceType,
    EncounterChargePayerRoute,
    EncounterReceiptDeliveryStatus,
} from '@pamosi/shared';
import { ApiError, chargesApi, encounterDocumentsApi } from '../lib/api';

const SERVICE_LABELS: Record<EncounterChargeServiceType, string> = {
    consultation: 'Consultation',
    medication: 'Medication',
    lab: 'Lab',
    procedure: 'Procedure',
    other: 'Other',
};

const PAYER_LABELS: Record<EncounterChargePayerRoute, string> = {
    self_pay: 'Self-pay',
    hmo: 'HMO',
    corporate: 'Corporate',
    family: 'Family',
};

function formatNaira(kobo: number): string {
    const naira = kobo / 100;
    return `₦${naira.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function EncounterChargesSection({
    encounterId,
    patientName,
    clinicName,
    writable,
    hasMedications = false,
    hasLabs = false,
    // Default true — server enforces patient consent and returns a clear
    // error code if missing. Keeping the UI unblocked avoids a second
    // fetch just to compute the disabled state.
    patientHasVaultConsent = true,
    patientEmail = null,
}: {
    encounterId: string;
    patientName: string | null;
    clinicName: string | null;
    writable: boolean;
    hasMedications?: boolean;
    hasLabs?: boolean;
    patientHasVaultConsent?: boolean;
    patientEmail?: string | null;
}) {
    const qc = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ['encounter-charges', encounterId],
        queryFn: () => chargesApi.list(encounterId),
    });

    const charges = data?.charges ?? [];
    const liveCharges = useMemo(
        () => charges.filter((c) => c.status !== 'void'),
        [charges],
    );
    const subtotal = useMemo(
        () => liveCharges.reduce((sum, c) => sum + (c.total_kobo || 0), 0),
        [liveCharges],
    );
    const outstanding = useMemo(
        () => liveCharges
            .filter((c) => c.status === 'unpaid')
            .reduce((sum, c) => sum + (c.total_kobo || 0) - (c.paid_kobo || 0), 0),
        [liveCharges],
    );

    function invalidate() {
        return qc.invalidateQueries({ queryKey: ['encounter-charges', encounterId] });
    }

    // Delivery status derived from the first live charge — all live rows get
    // the same stamp during delivery (server-side), so any row is representative.
    const deliveryStatus: EncounterReceiptDeliveryStatus = liveCharges[0]?.receipt_delivery_status ?? 'none';
    const deliveredAt = liveCharges[0]?.receipt_delivered_at ?? null;

    return (
        <div className="pm-card p-5">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                        Charges & receipt
                    </div>
                    <div className="text-slate-500 text-xs mt-1">
                        Line-item billing for this visit. Deliver as PDF, to Pamosi
                        Vault (if patient has consented), or by email.
                    </div>
                    <DeliveryStatusChip status={deliveryStatus} deliveredAt={deliveredAt} />
                </div>
                <button
                    type="button"
                    className="pm-btn-outline text-xs"
                    disabled={liveCharges.length === 0}
                    onClick={() => openReceiptWindow({
                        clinicName: clinicName ?? 'Pamosi Clinic',
                        patientName: patientName ?? '—',
                        encounterId,
                        charges: liveCharges,
                        subtotalKobo: subtotal,
                        outstandingKobo: outstanding,
                    })}
                >
                    Print (browser)
                </button>
            </div>

            <ReceiptDeliveryBar
                encounterId={encounterId}
                hasLiveCharges={liveCharges.length > 0}
                hasMedications={hasMedications}
                hasLabs={hasLabs}
                patientHasVaultConsent={patientHasVaultConsent}
                defaultEmail={patientEmail}
                onDelivered={invalidate}
            />

            <div className="mt-4">
                {isLoading ? (
                    <div className="text-slate-500 text-sm">Loading charges…</div>
                ) : charges.length === 0 ? (
                    <div className="text-slate-500 text-sm">No charges on this encounter yet.</div>
                ) : (
                    <ChargesTable
                        charges={charges}
                        encounterId={encounterId}
                        writable={writable}
                        onChanged={invalidate}
                    />
                )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-4 text-sm">
                <div className="text-slate-500">Outstanding</div>
                <div className="text-slate-900 font-medium">{formatNaira(outstanding)}</div>
                <div className="text-slate-500 ml-4">Total</div>
                <div className="text-slate-900 font-semibold">{formatNaira(subtotal)}</div>
            </div>

            {writable && (
                <AddChargeForm encounterId={encounterId} onAdded={invalidate} />
            )}
            <ClinicInvoicePanel encounterId={encounterId} />
        </div>
    );
}

function ChargesTable({
    charges, encounterId, writable, onChanged,
}: {
    charges: EncounterCharge[];
    encounterId: string;
    writable: boolean;
    onChanged: () => Promise<unknown>;
}) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-slate-500 text-xs uppercase tracking-wide">
                        <th className="py-2 pr-3 font-medium">Item</th>
                        <th className="py-2 pr-3 font-medium">Type</th>
                        <th className="py-2 pr-3 font-medium">Qty</th>
                        <th className="py-2 pr-3 font-medium">Unit</th>
                        <th className="py-2 pr-3 font-medium">Total</th>
                        <th className="py-2 pr-3 font-medium">Payer</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 font-medium">&nbsp;</th>
                    </tr>
                </thead>
                <tbody>
                    {charges.map((c) => (
                        <ChargeRow
                            key={c.id}
                            charge={c}
                            encounterId={encounterId}
                            writable={writable}
                            onChanged={onChanged}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ChargeRow({
    charge, encounterId, writable, onChanged,
}: {
    charge: EncounterCharge;
    encounterId: string;
    writable: boolean;
    onChanged: () => Promise<unknown>;
}) {
    const [actionError, setActionError] = useState<string | null>(null);

    const markPaid = useMutation({
        mutationFn: () => chargesApi.markPaid(encounterId, charge.id),
        onSuccess: () => onChanged(),
        onError: (err) => setActionError(prettyError(err)),
    });

    const voidMut = useMutation({
        mutationFn: (reason: string | null) => chargesApi.void(encounterId, charge.id, reason),
        onSuccess: () => onChanged(),
        onError: (err) => setActionError(prettyError(err)),
    });

    const statusColor =
        charge.status === 'paid' ? 'text-emerald-700 bg-emerald-50' :
        charge.status === 'void' ? 'text-slate-500 bg-slate-100' :
        'text-amber-700 bg-amber-50';

    return (
        <tr className="border-t border-slate-100 align-top">
            <td className="py-2 pr-3">
                <div className="text-slate-900">{charge.label}</div>
                {charge.note && (
                    <div className="text-slate-500 text-xs mt-0.5">{charge.note}</div>
                )}
                {actionError && (
                    <div className="pm-error text-xs mt-1">{actionError}</div>
                )}
            </td>
            <td className="py-2 pr-3 text-slate-600">{SERVICE_LABELS[charge.service_type]}</td>
            <td className="py-2 pr-3 text-slate-600 tabular-nums">{charge.quantity}</td>
            <td className="py-2 pr-3 text-slate-600 tabular-nums">{formatNaira(charge.unit_price_kobo)}</td>
            <td className="py-2 pr-3 text-slate-900 tabular-nums">{formatNaira(charge.total_kobo)}</td>
            <td className="py-2 pr-3 text-slate-600">{PAYER_LABELS[charge.payer_route]}</td>
            <td className="py-2 pr-3">
                <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${statusColor}`}>
                    {charge.status}
                </span>
            </td>
            <td className="py-2">
                {writable && charge.status === 'unpaid' && !charge.invoice_id && (
                    <div className="flex gap-1 justify-end">
                        <button
                            type="button"
                            className="pm-btn-outline text-xs py-1 px-2"
                            disabled={markPaid.isPending}
                            onClick={() => { setActionError(null); markPaid.mutate(); }}
                        >
                            {markPaid.isPending ? '…' : 'Mark paid'}
                        </button>
                        <button
                            type="button"
                            className="pm-btn-outline text-xs py-1 px-2 text-rose-700"
                            disabled={voidMut.isPending}
                            onClick={() => {
                                const reason = window.prompt('Void reason (optional)') ?? null;
                                setActionError(null);
                                voidMut.mutate(reason?.trim() || null);
                            }}
                        >
                            Void
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
}

function AddChargeForm({
    encounterId, onAdded,
}: {
    encounterId: string;
    onAdded: () => Promise<unknown>;
}) {
    const [serviceType, setServiceType] = useState<EncounterChargeServiceType>('consultation');
    const [label, setLabel] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [priceNaira, setPriceNaira] = useState('');
    const [payerRoute, setPayerRoute] = useState<EncounterChargePayerRoute>('self_pay');
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const mut = useMutation({
        mutationFn: async () => {
            const qty = Number(quantity);
            const priceNairaNum = Number(priceNaira);
            if (!label.trim()) throw new ApiError(400, 'invalid_label');
            if (!Number.isFinite(qty) || qty <= 0) throw new ApiError(400, 'invalid_quantity');
            if (!Number.isFinite(priceNairaNum) || priceNairaNum < 0) throw new ApiError(400, 'invalid_unit_price');
            return chargesApi.create(encounterId, {
                service_type: serviceType,
                label: label.trim(),
                quantity: qty,
                unit_price_kobo: Math.round(priceNairaNum * 100),
                payer_route: payerRoute,
                note: note.trim() || null,
            });
        },
        onSuccess: async () => {
            setLabel('');
            setQuantity('1');
            setPriceNaira('');
            setNote('');
            setError(null);
            await onAdded();
        },
        onError: (err) => setError(prettyError(err)),
    });

    return (
        <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                Add line
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                <select
                    className="pm-input"
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value as EncounterChargeServiceType)}
                    aria-label="Service type"
                >
                    {Object.entries(SERVICE_LABELS).map(([v, label]) => (
                        <option key={v} value={v}>{label}</option>
                    ))}
                </select>
                <input
                    className="pm-input md:col-span-2"
                    placeholder="Item description"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    aria-label="Label"
                />
                <input
                    className="pm-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="Qty"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    aria-label="Quantity"
                />
                <input
                    className="pm-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Price (₦)"
                    value={priceNaira}
                    onChange={(e) => setPriceNaira(e.target.value)}
                    aria-label="Unit price"
                />
                <select
                    className="pm-input"
                    value={payerRoute}
                    onChange={(e) => setPayerRoute(e.target.value as EncounterChargePayerRoute)}
                    aria-label="Payer route"
                >
                    {Object.entries(PAYER_LABELS).map(([v, label]) => (
                        <option key={v} value={v}>{label}</option>
                    ))}
                </select>
            </div>
            <div className="mt-2 flex gap-2">
                <input
                    className="pm-input flex-1"
                    placeholder="Note (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    aria-label="Note"
                />
                <button
                    type="button"
                    className="pm-btn-primary text-sm"
                    disabled={mut.isPending || !label.trim() || !priceNaira}
                    onClick={() => { setError(null); mut.mutate(); }}
                >
                    {mut.isPending ? 'Adding…' : 'Add line'}
                </button>
            </div>
            {error && <div className="pm-error text-xs mt-2">{error}</div>}
        </div>
    );
}

function openReceiptWindow(args: {
    clinicName: string;
    patientName: string;
    encounterId: string;
    charges: EncounterCharge[];
    subtotalKobo: number;
    outstandingKobo: number;
}) {
    // Browser pop-up blocked by default on gestures without user intent —
    // this is a direct click so most browsers will allow it. If it fails
    // the clinician can retry; no fallback in 3.c₁.
    const win = window.open('', '_blank', 'width=720,height=900');
    if (!win) return;
    const now = new Date().toLocaleString();
    const rows = args.charges.map((c) => `
        <tr>
          <td>${escapeHtml(c.label)}${c.note ? `<div class="note">${escapeHtml(c.note)}</div>` : ''}</td>
          <td>${SERVICE_LABELS[c.service_type]}</td>
          <td class="num">${c.quantity}</td>
          <td class="num">${formatNaira(c.unit_price_kobo)}</td>
          <td class="num">${formatNaira(c.total_kobo)}</td>
          <td>${PAYER_LABELS[c.payer_route]}</td>
          <td>${c.status === 'unpaid' && (c.paid_kobo || 0) > 0 ? 'part paid' : c.status}</td>
        </tr>
    `).join('');
    win.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Receipt — ${escapeHtml(args.patientName)}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #0f172a; margin: 24px; }
    h1 { font-size: 20px; margin: 0 0 4px; }
    .meta { color: #64748b; font-size: 12px; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 13px; }
    th, td { padding: 8px; border-bottom: 1px solid #e2e8f0; text-align: left; vertical-align: top; }
    th { background: #f8fafc; text-transform: uppercase; font-size: 10px; letter-spacing: 0.04em; color: #475569; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .note { color: #64748b; font-size: 11px; margin-top: 2px; }
    .totals { margin-top: 16px; text-align: right; font-size: 14px; }
    .totals .line { margin-top: 4px; }
    .totals .grand { font-weight: 600; font-size: 16px; }
    .footer { margin-top: 32px; color: #94a3b8; font-size: 11px; }
    @media print {
      body { margin: 12mm; }
      button { display: none; }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(args.clinicName)}</h1>
  <div class="meta">
    Patient: ${escapeHtml(args.patientName)}<br/>
    Encounter: ${escapeHtml(args.encounterId)}<br/>
    Issued: ${escapeHtml(now)}
  </div>
  <table>
    <thead>
      <tr>
        <th>Item</th><th>Type</th><th>Qty</th><th>Unit</th><th>Total</th><th>Payer</th><th>Status</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="totals">
    <div class="line">Outstanding: ${formatNaira(args.outstandingKobo)}</div>
    <div class="line grand">Total: ${formatNaira(args.subtotalKobo)}</div>
  </div>
  <div class="footer">Printed from Pamosi Clinic.</div>
  <script>setTimeout(() => window.print(), 150);</script>
</body>
</html>`);
    win.document.close();
}

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function prettyError(err: unknown): string {
    if (err instanceof ApiError) {
        switch (err.code) {
            case 'invalid_label': return 'Enter an item description.';
            case 'invalid_quantity': return 'Quantity must be positive.';
            case 'invalid_unit_price': return 'Price must be zero or more.';
            case 'invalid_service_type': return 'Pick a service type.';
            case 'invalid_payer_route': return 'Pick a payer.';
            case 'invalid_transition': return 'This charge cannot change state anymore.';
            case 'insufficient_role': return 'Your role cannot do that.';
            case 'no_live_charges': return 'Add at least one unpaid or paid charge first.';
            case 'patient_has_no_vault_consent': return 'Patient has not linked their Pamosi account yet.';
            case 'invalid_email': return 'Enter a valid email address.';
            case 'no_medications': return 'No medications on this encounter.';
            case 'no_lab_requests': return 'No lab orders on this encounter.';
        }
    }
    return 'Could not save. Try again.';
}

// ─── Phase 3.c₂: receipt delivery + prescription/lab PDFs ───────────────

const DELIVERY_LABEL: Record<EncounterReceiptDeliveryStatus, string> = {
    none: 'Not generated',
    printed: 'PDF generated',
    sent_to_vault: 'Sent to Vault',
    emailed: 'Emailed',
};

function DeliveryStatusChip({
    status, deliveredAt,
}: {
    status: EncounterReceiptDeliveryStatus;
    deliveredAt: string | null;
}) {
    const color =
        status === 'sent_to_vault' ? 'bg-emerald-50 text-emerald-700' :
        status === 'emailed'       ? 'bg-sky-50 text-sky-700' :
        status === 'printed'       ? 'bg-amber-50 text-amber-700' :
                                     'bg-slate-100 text-slate-500';
    const when = deliveredAt ? new Date(deliveredAt).toLocaleString() : null;
    return (
        <div className="mt-2 inline-flex items-center gap-2 text-xs">
            <span className={`inline-block rounded px-2 py-0.5 font-medium ${color}`}>
                Receipt: {DELIVERY_LABEL[status]}
            </span>
            {when && <span className="text-slate-500">· {when}</span>}
        </div>
    );
}

function ReceiptDeliveryBar({
    encounterId, hasLiveCharges, hasMedications, hasLabs,
    patientHasVaultConsent, defaultEmail, onDelivered,
}: {
    encounterId: string;
    hasLiveCharges: boolean;
    hasMedications: boolean;
    hasLabs: boolean;
    patientHasVaultConsent: boolean;
    defaultEmail: string | null;
    onDelivered: () => Promise<unknown>;
}) {
    const [busy, setBusy] = useState<null | 'pdf' | 'vault' | 'email' | 'rx' | 'lab'>(null);
    const [error, setError] = useState<string | null>(null);

    async function openSignedUrl(url: string) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    async function downloadReceipt() {
        setBusy('pdf'); setError(null);
        try {
            const res = await encounterDocumentsApi.renderReceiptPdf(encounterId);
            await openSignedUrl(res.signed_url);
            await onDelivered();
        } catch (err) { setError(prettyError(err)); }
        finally { setBusy(null); }
    }

    async function sendToVault() {
        setBusy('vault'); setError(null);
        try {
            const res = await encounterDocumentsApi.deliverReceipt(encounterId, { channel: 'vault' });
            await openSignedUrl(res.signed_url);
            await onDelivered();
        } catch (err) { setError(prettyError(err)); }
        finally { setBusy(null); }
    }

    async function emailReceipt() {
        const to = window.prompt('Email receipt to:', defaultEmail ?? '');
        if (!to || !to.trim()) return;
        setBusy('email'); setError(null);
        try {
            const res = await encounterDocumentsApi.deliverReceipt(encounterId, {
                channel: 'email', email: to.trim(),
            });
            await openSignedUrl(res.signed_url);
            await onDelivered();
        } catch (err) { setError(prettyError(err)); }
        finally { setBusy(null); }
    }

    async function downloadPrescription() {
        setBusy('rx'); setError(null);
        try {
            const res = await encounterDocumentsApi.renderPrescriptionPdf(encounterId);
            await openSignedUrl(res.signed_url);
        } catch (err) { setError(prettyError(err)); }
        finally { setBusy(null); }
    }

    async function downloadLabRequest() {
        setBusy('lab'); setError(null);
        try {
            const res = await encounterDocumentsApi.renderLabRequestPdf(encounterId);
            await openSignedUrl(res.signed_url);
        } catch (err) { setError(prettyError(err)); }
        finally { setBusy(null); }
    }

    return (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
                type="button"
                className="pm-btn-outline text-xs"
                disabled={!hasLiveCharges || busy !== null}
                onClick={() => void downloadReceipt()}
                title="Render receipt as PDF (signed download link)"
            >
                {busy === 'pdf' ? '…' : 'Download receipt PDF'}
            </button>
            <button
                type="button"
                className="pm-btn-outline text-xs"
                disabled={!hasLiveCharges || !patientHasVaultConsent || busy !== null}
                onClick={() => void sendToVault()}
                title={
                    !patientHasVaultConsent
                        ? 'Patient has not linked their Pamosi account'
                        : 'Save receipt into the patient\'s Pamosi Vault'
                }
            >
                {busy === 'vault' ? '…' : 'Send to Vault'}
            </button>
            <button
                type="button"
                className="pm-btn-outline text-xs"
                disabled={!hasLiveCharges || busy !== null}
                onClick={() => void emailReceipt()}
            >
                {busy === 'email' ? '…' : 'Email receipt'}
            </button>
            {hasMedications && (
                <button
                    type="button"
                    className="pm-btn-outline text-xs ml-auto"
                    disabled={busy !== null}
                    onClick={() => void downloadPrescription()}
                >
                    {busy === 'rx' ? '…' : 'Prescription PDF'}
                </button>
            )}
            {hasLabs && (
                <button
                    type="button"
                    className="pm-btn-outline text-xs"
                    disabled={busy !== null}
                    onClick={() => void downloadLabRequest()}
                >
                    {busy === 'lab' ? '…' : 'Lab request PDF'}
                </button>
            )}
            {error && (
                <div className="w-full text-rose-700 text-xs mt-1">{error}</div>
            )}
        </div>
    );
}
